import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import {
  formatDimensionCm,
  getPrestigeExternalDepthCm,
  getPrestigeExternalWidthCm,
  type UnitType,
} from '@domain/services/prestigeDimensionDisplay';
import type { PrestigeService } from '@infrastructure/three/prestigeService';

type Props = {
  service: PrestigeService | null;
  getCamera: () => THREE.PerspectiveCamera | null;
  widthValue: number;
  depthValue: number;
  nightMode: boolean;
  unit: UnitType;
  visible: boolean;
};

type AxisObjects = {
  lines: LineSegments2[];
  labels: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>[];
  labelAspect: number;
};

const DEFAULT_X_SIDE: 1 | -1 = -1;
const DEFAULT_Z_SIDE: 1 | -1 = -1;
const BASE_LINE_OPACITY = 0.9;
const AXIS_BLEND_POWER = 2.4;
const TOP_LINE_Y_OFFSET_FACTOR = 0.58;
const LABEL_HEIGHT_SCALE = 0.085;
const LABEL_HEIGHT_MIN = 0.16;
const LABEL_HEIGHT_MAX = 0.28;
const LABEL_Y_OFFSET_FACTOR = 0.5; // Position label above the dimension line

const createLine = (lineColor: number) => {
  const geometry = new LineSegmentsGeometry();
  geometry.setPositions(new Float32Array(18));

  const material = new LineMaterial({
    color: lineColor,
    transparent: true,
    opacity: BASE_LINE_OPACITY,
    depthTest: false,
    linewidth: 0.012,
    worldUnits: true,
  });
  material.resolution.set(window.innerWidth, window.innerHeight);

  const line = new LineSegments2(geometry, material);
  line.renderOrder = 8;
  line.userData.dimensionOverlay = true;
  return line;
};

const updateLinePoints = (
  line: LineSegments2,
  points: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3]
) => {
  const geometry = line.geometry as LineSegmentsGeometry;
  const material = line.material as LineMaterial;
  material.resolution.set(window.innerWidth, window.innerHeight);

  const positions = new Float32Array(18);
  let index = 0;
  for (const point of points) {
    positions[index] = point.x;
    positions[index + 1] = point.y;
    positions[index + 2] = point.z;
    index += 3;
  }
  geometry.setPositions(positions);
  geometry.computeBoundingSphere();
};

const createLabelTexture = (
  text: string,
  textColor: string
): { texture: THREE.CanvasTexture; aspect: number } => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    const fallback = document.createElement('canvas');
    fallback.width = 256;
    fallback.height = 96;
    const texture = new THREE.CanvasTexture(fallback);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    return { texture, aspect: fallback.width / fallback.height };
  }

  const fontSizePx = 26;
  const paddingX = 20;
  const paddingY = 10;
  context.font = `500 ${fontSizePx}px Roboto, "Avenir Next", "Segoe UI", sans-serif`;
  const textWidth = Math.ceil(context.measureText(text).width);

  canvas.width = Math.max(256, textWidth + paddingX * 2);
  canvas.height = fontSizePx + paddingY * 2;

  const drawContext = canvas.getContext('2d');
  if (!drawContext) {
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    return { texture, aspect: canvas.width / canvas.height };
  }

  drawContext.clearRect(0, 0, canvas.width, canvas.height);
  drawContext.font = `600 ${fontSizePx}px Roboto, "Avenir Next", "Segoe UI", sans-serif`;
  drawContext.fillStyle = textColor;
  drawContext.textAlign = 'center';
  drawContext.textBaseline = 'middle';
  drawContext.fillText(text, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return { texture, aspect: canvas.width / canvas.height };
};

const createLabelMesh = (texture: THREE.CanvasTexture): THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> => {
  const geometry = new THREE.PlaneGeometry(1, 1);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    side: THREE.DoubleSide,
    depthTest: true,
    depthWrite: false,
    opacity: 0,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.renderOrder = 9;
  mesh.userData.dimensionOverlay = true;
  return mesh;
};

const disposeLabelMesh = (mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>) => {
  const material = mesh.material;
  if (material.map) material.map.dispose();
  material.dispose();
  mesh.geometry.dispose();
};

const createAxisObjects = (
  labelText: string,
  labelCount: number,
  lineCount: number,
  textColor: string,
  lineColor: number
): AxisObjects => {
  const { texture, aspect } = createLabelTexture(labelText, textColor);
  const labels = Array.from({ length: labelCount }, (_, index) => {
    const labelTexture = index === 0 ? texture : texture.clone();
    labelTexture.needsUpdate = true;
    return createLabelMesh(labelTexture);
  });
  const lines = Array.from({ length: lineCount }, () => createLine(lineColor));
  return {
    lines,
    labels,
    labelAspect: aspect,
  };
};

const disposeAxisObjects = (axis: AxisObjects) => {
  axis.lines.forEach((line) => {
    (line.geometry as LineSegmentsGeometry).dispose();
    (line.material as LineMaterial).dispose();
  });
  axis.labels.forEach(disposeLabelMesh);
};

const updateLabelOpacity = (
  label: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>,
  targetOpacity: number
) => {
  const material = label.material;
  material.opacity = THREE.MathUtils.clamp(targetOpacity, 0, 1);
  label.visible = true;
};

const updateLineOpacity = (line: LineSegments2, targetOpacity: number) => {
  const material = line.material as LineMaterial;
  material.opacity = BASE_LINE_OPACITY * THREE.MathUtils.clamp(targetOpacity, 0, 1);
};

export const DimensionOverlay = ({
  service,
  getCamera,
  widthValue,
  depthValue,
  nightMode,
  unit,
  visible,
}: Props) => {
  const labels = useMemo(
    () => ({
      width: `Width (${formatDimensionCm(getPrestigeExternalWidthCm(widthValue), unit)})`,
      length: `Depth (${formatDimensionCm(getPrestigeExternalDepthCm(depthValue), unit)})`,
    }),
    [depthValue, unit, widthValue]
  );

  useEffect(() => {
    if (!service || !visible) return;

    const hostGroup = service.getGroup();
    const overlayGroup = new THREE.Group();
    overlayGroup.name = 'DimensionOverlay3D';
    overlayGroup.userData.dimensionOverlay = true;
    const labelTextColor = nightMode ? 'rgba(247, 249, 239, 0.98)' : 'rgba(24, 24, 24, 0.95)';
    const lineColor = nightMode ? 0xf7f9ef : 0x202020;

    const widthAxis = createAxisObjects(labels.width, 2, 2, labelTextColor, lineColor);
    const lengthAxis = createAxisObjects(labels.length, 2, 2, labelTextColor, lineColor);

    overlayGroup.add(...widthAxis.lines, ...widthAxis.labels);
    overlayGroup.add(...lengthAxis.lines, ...lengthAxis.labels);
    hostGroup.add(overlayGroup);

    const cameraWorld = new THREE.Vector3();

    const widthStart = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const widthEnd = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const widthTickStartA = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const widthTickStartB = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const widthTickEndA = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const widthTickEndB = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const widthLabelPos = [new THREE.Vector3(), new THREE.Vector3()] as const;

    const lengthStart = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const lengthEnd = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const lengthTickStartA = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const lengthTickStartB = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const lengthTickEndA = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const lengthTickEndB = [new THREE.Vector3(), new THREE.Vector3()] as const;
    const lengthLabelPos = [new THREE.Vector3(), new THREE.Vector3()] as const;

    let frame = 0;
    const updateOverlay = () => {
      const center = service.getModelCenter();
      const size = service.getModelSize();
      const halfX = size.x / 2;
      const halfY = size.y / 2;
      const halfZ = size.z / 2;
      const maxSize = Math.max(size.x, size.y, size.z);
      const axisOffset = THREE.MathUtils.clamp(maxSize * 0.08, 0.16, 0.48);
      const topLineYOffset = axisOffset * TOP_LINE_Y_OFFSET_FACTOR;
      const tickHalf = THREE.MathUtils.clamp(maxSize * 0.03, 0.04, 0.12);
      const labelHeight = THREE.MathUtils.clamp(maxSize * LABEL_HEIGHT_SCALE, LABEL_HEIGHT_MIN, LABEL_HEIGHT_MAX);
      const labelGap = THREE.MathUtils.clamp(maxSize * 0.06, 0.08, 0.22);
      const offsetX = halfX + axisOffset;
      const offsetZ = halfZ + axisOffset;
      const camera = getCamera();
      if (camera) {
        camera.getWorldPosition(cameraWorld);
      }
      let cameraWeightX = DEFAULT_X_SIDE;
      let cameraWeightZ = DEFAULT_Z_SIDE;
      if (camera) {
        const dx = cameraWorld.x - center.x;
        const dz = cameraWorld.z - center.z;
        const planarLength = Math.hypot(dx, dz);
        if (planarLength > 1e-5) {
          cameraWeightX = dx / planarLength;
          cameraWeightZ = dz / planarLength;
        }
      }

      const absX = Math.abs(cameraWeightX);
      const absZ = Math.abs(cameraWeightZ);
      const xPower = Math.pow(absX, AXIS_BLEND_POWER);
      const zPower = Math.pow(absZ, AXIS_BLEND_POWER);
      const axisPowerTotal = xPower + zPower;
      const axisXFactor = axisPowerTotal > 1e-8 ? xPower / axisPowerTotal : 0.5;
      const axisZFactor = axisPowerTotal > 1e-8 ? zPower / axisPowerTotal : 0.5;

      const hasWall = service.getHasWall();
      const widthSides: Array<1 | -1> = [-1, 1];
      const WALL_SIDE_Z = -1; // North/back side where wall is attached
      const widthOpacities = widthSides.map((zSide) => {
        if (hasWall && zSide === WALL_SIDE_Z) return 0;
        const sideFactor = THREE.MathUtils.clamp((1 + zSide * cameraWeightZ) * 0.5, 0, 1);
        return axisZFactor * sideFactor;
      });
      widthAxis.lines.forEach((line, index) => {
        const zSide = widthSides[index] ?? DEFAULT_Z_SIDE;
        widthStart[index].set(
          center.x - offsetX,
          center.y + halfY + topLineYOffset,
          center.z + zSide * offsetZ
        );
        widthEnd[index].set(
          center.x + offsetX,
          center.y + halfY + topLineYOffset,
          center.z + zSide * offsetZ
        );
        widthTickStartA[index].set(widthStart[index].x, widthStart[index].y - tickHalf, widthStart[index].z);
        widthTickStartB[index].set(widthStart[index].x, widthStart[index].y + tickHalf, widthStart[index].z);
        widthTickEndA[index].set(widthEnd[index].x, widthEnd[index].y - tickHalf, widthEnd[index].z);
        widthTickEndB[index].set(widthEnd[index].x, widthEnd[index].y + tickHalf, widthEnd[index].z);
        updateLinePoints(line, [
          widthStart[index],
          widthEnd[index],
          widthTickStartA[index],
          widthTickStartB[index],
          widthTickEndA[index],
          widthTickEndB[index],
        ]);
        updateLineOpacity(line, widthOpacities[index] ?? 0);
      });
      widthAxis.labels.forEach((label, index) => {
        const zSide = widthSides[index] ?? DEFAULT_Z_SIDE;
        widthLabelPos[index].set(center.x, center.y + halfY + topLineYOffset, center.z + zSide * offsetZ);
        widthLabelPos[index].y += labelHeight * LABEL_Y_OFFSET_FACTOR;
        widthLabelPos[index].z += zSide * labelGap;
        label.position.copy(widthLabelPos[index]);
        label.rotation.set(0, zSide === 1 ? 0 : Math.PI, 0);
        label.scale.set(labelHeight * widthAxis.labelAspect, labelHeight, 1);
        updateLabelOpacity(label, widthOpacities[index] ?? 0);
      });

      const lengthSides: Array<1 | -1> = [-1, 1];
      const lengthOpacities = lengthSides.map((xSide) => {
        const sideFactor = THREE.MathUtils.clamp((1 + xSide * cameraWeightX) * 0.5, 0, 1);
        return axisXFactor * sideFactor;
      });
      lengthAxis.lines.forEach((line, index) => {
        const xSide = lengthSides[index] ?? DEFAULT_X_SIDE;
        lengthStart[index].set(
          center.x + xSide * offsetX,
          center.y + halfY + topLineYOffset,
          center.z - offsetZ
        );
        lengthEnd[index].set(
          center.x + xSide * offsetX,
          center.y + halfY + topLineYOffset,
          center.z + offsetZ
        );
        lengthTickStartA[index].set(lengthStart[index].x, lengthStart[index].y - tickHalf, lengthStart[index].z);
        lengthTickStartB[index].set(lengthStart[index].x, lengthStart[index].y + tickHalf, lengthStart[index].z);
        lengthTickEndA[index].set(lengthEnd[index].x, lengthEnd[index].y - tickHalf, lengthEnd[index].z);
        lengthTickEndB[index].set(lengthEnd[index].x, lengthEnd[index].y + tickHalf, lengthEnd[index].z);
        updateLinePoints(line, [
          lengthStart[index],
          lengthEnd[index],
          lengthTickStartA[index],
          lengthTickStartB[index],
          lengthTickEndA[index],
          lengthTickEndB[index],
        ]);
        updateLineOpacity(line, lengthOpacities[index] ?? 0);
      });
      lengthAxis.labels.forEach((label, index) => {
        const xSide = lengthSides[index] ?? DEFAULT_X_SIDE;
        lengthLabelPos[index].set(center.x + xSide * offsetX, center.y + halfY + topLineYOffset, center.z);
        lengthLabelPos[index].y += labelHeight * LABEL_Y_OFFSET_FACTOR;
        lengthLabelPos[index].x += xSide * labelGap;
        label.position.copy(lengthLabelPos[index]);
        label.rotation.set(0, xSide === 1 ? Math.PI / 2 : -Math.PI / 2, 0);
        label.scale.set(labelHeight * lengthAxis.labelAspect, labelHeight, 1);
        updateLabelOpacity(label, lengthOpacities[index] ?? 0);
      });

      frame = requestAnimationFrame(updateOverlay);
    };

    frame = requestAnimationFrame(updateOverlay);

    return () => {
      cancelAnimationFrame(frame);
      hostGroup.remove(overlayGroup);
      disposeAxisObjects(widthAxis);
      disposeAxisObjects(lengthAxis);
    };
  }, [getCamera, labels.length, labels.width, nightMode, service, visible]);

  return null;
};
